import type { NetworkSource, NetworkPath } from './network-accounting.js';
export interface OriginFetchOptions {
  /** Reports payload as it arrives, including bytes received before cancellation. */
  onBytesReceived?: (bytes: number) => void;
  signal: AbortSignal;
  /** Live-edge playback request (urgent DataChannel) vs background pull. */
  urgent: boolean;
}

/**
 * Where the scheduler gets a segment when no viewer peer delivers it in time.
 *
 * `fetch` resolves with the encrypted segment bytes, or stays pending until
 * `signal` aborts when the bytes arrive through another path (the scheduler
 * aborts the fetch once any peer, including the seed, delivers the segment).
 * It must reject on failure, timeout, or abort (with `name === 'AbortError'`).
 */
export interface OriginSource {
  readonly networkSource?: NetworkSource;
  readonly networkPath?: NetworkPath;
  fetch(segmentId: string, opts: OriginFetchOptions): Promise<ArrayBuffer>;
}

export function abortError(): Error {
  const err = new Error('Aborted');
  err.name = 'AbortError';
  return err;
}

/** Engine hooks SeedOriginSource needs to reach the seed over its DataChannel. */
export interface SeedOriginHost {
  /** Seed peer ids with an open DataChannel. */
  getConnectedSeedIds(): string[];
  /** Ask a seed for a segment over the DataChannel. False if the channel is not open. */
  requestFromSeed(seedPeerId: string, segmentId: string, urgent: boolean): boolean;
}

/**
 * Viewer origin = the studio seed. Sends `request-segment` over the DataChannel
 * of a connected `seed-*` peer. The reply is a regular peer segment, so it is
 * verified and delivered by `SegmentScheduler.handlePeerSegment`, which aborts
 * this fetch. If no seed is connected yet, the request is sent as soon as one
 * becomes available (`notifySeedAvailable`). Rejects after `timeoutMs`.
 *
 * A seed over its first-hop fan-out cap answers with a NACK and delivers the
 * segment on its next publish; `onNack` moves the request to another seed if
 * one is connected, and otherwise keeps waiting for that deferred delivery
 * (the engine meanwhile asks viewer peers).
 */
export class SeedOriginSource implements OriginSource {
  private readonly waiters = new Set<() => void>();
  /** In-flight fetches per segment: who they were sent to and who declined them. */
  private readonly inflight = new Map<string, Set<{ sentTo: string | null; nackedBy: Set<string>; retry: () => void }>>();

  constructor(
    private readonly host: SeedOriginHost,
    private readonly timeoutMs: number,
  ) {}

  /** Call when a seed DataChannel opens so queued requests are sent. */
  notifySeedAvailable(): void {
    for (const trySend of [...this.waiters]) trySend();
  }

  /**
   * A seed declined `segmentId`. Retries another connected seed that has not
   * declined it; returns true when an in-flight fetch for the segment exists.
   */
  onNack(segmentId: string, seedId: string): boolean {
    const states = this.inflight.get(segmentId);
    if (!states || states.size === 0) return false;
    for (const st of states) {
      if (st.sentTo !== seedId) continue;
      st.nackedBy.add(seedId);
      st.retry();
    }
    return true;
  }

  fetch(segmentId: string, { signal, urgent }: OriginFetchOptions): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((_resolve, reject) => {
      if (signal.aborted) {
        reject(abortError());
        return;
      }
      const state = { sentTo: null as string | null, nackedBy: new Set<string>(), retry: () => {} };
      let sent = false;
      const trySend = () => {
        if (sent) return;
        for (const seedId of this.host.getConnectedSeedIds()) {
          if (state.nackedBy.has(seedId)) continue;
          if (this.host.requestFromSeed(seedId, segmentId, urgent)) {
            sent = true;
            state.sentTo = seedId;
            this.waiters.delete(trySend);
            return;
          }
        }
      };
      state.retry = () => {
        const others = this.host.getConnectedSeedIds().some((id) => !state.nackedBy.has(id));
        if (!others) return; // keep waiting: the declining seed delivers on its next publish
        sent = false;
        trySend();
      };
      let states = this.inflight.get(segmentId);
      if (!states) {
        states = new Set();
        this.inflight.set(segmentId, states);
      }
      states.add(state);
      const cleanup = () => {
        clearTimeout(timer);
        this.waiters.delete(trySend);
        signal.removeEventListener('abort', onAbort);
        const set = this.inflight.get(segmentId);
        set?.delete(state);
        if (set && set.size === 0) this.inflight.delete(segmentId);
      };
      const onAbort = () => {
        cleanup();
        reject(abortError());
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(
          new Error(
            sent
              ? `Seed did not deliver ${segmentId} within ${this.timeoutMs}ms`
              : `No seed connected to fetch ${segmentId}`,
          ),
        );
      }, this.timeoutMs);
      signal.addEventListener('abort', onAbort, { once: true });
      this.waiters.add(trySend);
      trySend();
    });
  }
}

export interface HttpOriginOptions {
  /** Address of a segment, e.g. `${api}/api/streams/${streamId}/media/${segmentId}`. */
  url(segmentId: string): string;
  /** Rejects after this long without the bytes. */
  timeoutMs: number;
  /** Merged into every request (credentials, headers such as the viewer session). */
  init?: RequestInit;
  /** Wait between attempts while the origin does not have the segment yet (default 250 ms). */
  retryMs?: number;
  fetch?: typeof fetch;
}

/** Status codes after which asking again cannot help: the origin refuses this viewer or the broadcast is over. */
const HTTP_ORIGIN_FINAL = new Set([400, 401, 403, 410]);

/**
 * The media proxy will never have this segment (410 SEGMENT_GONE: the studio did not get it there, e.g. a part of a
 * dropped GOP). Not the end of the broadcast (that is a 410 with another code): the player goes on to the next
 * independent part.
 */
export class SegmentGoneError extends Error {
  readonly code = 'SEGMENT_GONE';
  constructor(readonly segmentId: string) {
    super(`Media proxy will never have ${segmentId} (410 SEGMENT_GONE)`);
    this.name = 'SegmentGoneError';
  }
}

/**
 * Viewer origin over HTTP, for a stream whose seed is hidden behind OMP's media proxy: GETs the segment's bytes
 * (encrypted as in the swarm; the scheduler verifies the announced SHA-256). A segment the origin does not have
 * yet (404: announced, still on its way from the studio), a 429, a 5xx or a network error is asked again every
 * `retryMs` until `timeoutMs`; 400/401/403/410 reject at once, 410 SEGMENT_GONE with a SegmentGoneError. Never falls
 * back to the seed.
 */
export class HttpOriginSource implements OriginSource {
  readonly networkSource = 'proxy' as const;
  readonly networkPath = 'direct' as const;
  private readonly doFetch: typeof fetch;

  constructor(private readonly options: HttpOriginOptions) {
    this.doFetch = options.fetch ?? ((input, init) => fetch(input, init));
  }

  async fetch(segmentId: string, { signal, onBytesReceived }: OriginFetchOptions): Promise<ArrayBuffer> {
    if (signal.aborted) throw abortError();
    const deadline = Date.now() + this.options.timeoutMs;
    const retryMs = this.options.retryMs ?? 250;
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    signal.addEventListener('abort', onAbort, { once: true });
    const timer = setTimeout(() => controller.abort(), this.options.timeoutMs);
    let last = 'no answer';
    try {
      for (;;) {
        try {
          const res = await this.doFetch(this.options.url(segmentId), { ...this.options.init, signal: controller.signal });
          if (res.ok) {
            if (!res.body || !onBytesReceived) {
              const data = await res.arrayBuffer();
              onBytesReceived?.(data.byteLength);
              return data;
            }
            const reader = res.body.getReader();
            const chunks: Uint8Array[] = [];
            let total = 0;
            try {
              for (;;) {
                const { value, done } = await reader.read();
                if (done) break;
                if (!value.byteLength) continue;
                onBytesReceived(value.byteLength);
                chunks.push(value); total += value.byteLength;
              }
            } finally { reader.releaseLock(); }
            const data = new Uint8Array(total);
            let offset = 0;
            for (const chunk of chunks) { data.set(chunk, offset); offset += chunk.byteLength; }
            return data.buffer;
          }
          last = `answered ${res.status}`;
          if (res.status === 410) {
            const code = await res.json().then((body: { code?: unknown }) => body?.code, () => undefined);
            if (code === 'SEGMENT_GONE') throw new SegmentGoneError(segmentId);
          } else {
            await res.body?.cancel().catch(() => undefined);
          }
          if (HTTP_ORIGIN_FINAL.has(res.status)) break;
        } catch (err) {
          if (err instanceof SegmentGoneError) throw err;
          if (signal.aborted) throw abortError();
          if (controller.signal.aborted) break;
          last = err instanceof Error ? err.message : String(err);
        }
        if (Date.now() + retryMs >= deadline) break;
        await new Promise<void>((resolve) => {
          const wait = setTimeout(resolve, retryMs);
          controller.signal.addEventListener('abort', () => {
            clearTimeout(wait);
            resolve();
          }, { once: true });
        });
        if (signal.aborted) throw abortError();
        if (controller.signal.aborted) break;
      }
      throw new Error(`Media proxy did not deliver ${segmentId}: ${last}`);
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
    }
  }
}

/** Default when no origin is configured: always rejects. */
export const NO_ORIGIN: OriginSource = {
  fetch: (segmentId) => Promise.reject(new Error(`No origin source configured for ${segmentId}`)),
};
